import React from 'react';
import { useParams } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { usePortalQuery } from '../../hooks/usePortalQuery';
import { listOrgDeliverables } from '../../lib/portalApi';
import { usePortalAuth } from '../../context/PortalAuth';
import { orgName } from '../../context/PortalOrg';
import { PageHeader } from '../../components/portal/PageHeader';
import { DeliverableList } from '../../components/portal/DeliverableList';
import { EmptyState, QueryState } from '../../components/portal/QueryState';
import { fileKind } from '../../utils/portalFormat';
import type { OrgRole } from '../../types/portal';

/** Every deliverable across the projects the person can see in this organisation. */
export function PortalDeliverables() {
  const {
    orgId = ''
  } = useParams<{
    orgId: string;
  }>();
  const {
    roleIn
  } = usePortalAuth();
  const role = roleIn(orgId) as OrgRole;
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listOrgDeliverables(orgId), [orgId]);
  const [query, setQuery] = React.useState('');
  const q = query.trim().toLowerCase();
  const shown = (data ?? []).filter(g => !q || [g.latest.name, g.latest.filename, g.project.name, fileKind(g.latest.mimeType, g.latest.filename)].some(v => v.toLowerCase().includes(q)));
  return <div data-id="element-2920" data-mp="30glfnf7zbfsk">
      <PageHeader eyebrow={orgName(orgId)} title="Deliverables" description="Latest versions first. Earlier versions stay available under each file." data-id="element-2921" data-mp="30h3hqshboo8e" />

      <div className="relative mt-6 max-w-md" data-id="element-2922" data-mp="2vis9gm6oelsa">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-2923" data-mp="2qhw6cau5f3nx" />
        <label htmlFor="deliverable-search" className="sr-only" data-id="element-2924" data-mp="2mhlp220woqtr">
          Search deliverables
        </label>
        <input id="deliverable-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name, project or type" className="h-11 w-full rounded-sm border border-line bg-charcoal pl-10 pr-3 text-[15px] text-ink placeholder:text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-2925" data-mp="2hjahjxy32sep" />
      </div>

      <div className="mt-6" data-id="element-2926" data-mp="13niuvc4cl80h">
        <QueryState loading={loading} error={error} data-id="element-2927" data-mp="put5cxpm1kym">
          {shown.length === 0 ? <EmptyState title={q ? 'Nothing matches that search' : 'No deliverables yet'} body={q ? 'Try a shorter search.' : 'Files shared on your projects will appear here.'} data-id="element-2928" data-mp="ut4fl9jthxhd" /> : <>
              <p className="mb-3 text-[13px] text-muted" aria-live="polite" data-id="element-2929" data-mp="1ml22qcf2kklb">
                {shown.length} deliverable{shown.length === 1 ? '' : 's'}
              </p>
              <DeliverableList orgId={orgId} role={role} groups={shown} showProject data-id="element-2930" data-mp="18n5a1r3qkybx" />
            </>}
        </QueryState>
      </div>
    </div>;
}